import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { navigationConfig } from '@/config/navigation';
import { NavigationMenuItem } from './NavigationMenu';
import { Button } from '@/components/ui/button';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { useTimesheetContext } from '@/contexts/TimesheetContext';
import { useDataSource } from '@/contexts/DataSourceContext';
import { approvalsApi } from '@/services/approvalsApi';

interface SidebarProps {
  currentPath: string;
  onNavigate: (path: string) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ currentPath, onNavigate }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { pendingApprovals } = useTimesheetContext();
  const { getScreenMode } = useDataSource();
  const [liveBadgeCount, setLiveBadgeCount] = useState<number | null>(null);

  const approvalsMode = getScreenMode('approvals');

  // Fetch live badge count from API when in live mode
  useEffect(() => {
    if (approvalsMode === 'live') {
      approvalsApi.getPendingApprovalsCount()
        .then(response => {
          setLiveBadgeCount(response.count);
        })
        .catch(() => {
          // Fall back to demo count on error
          setLiveBadgeCount(null);
        });
    } else {
      setLiveBadgeCount(null);
    }
  }, [approvalsMode]);

  // Use live count when in live mode, otherwise use demo count
  const badgeCount = approvalsMode === 'live' && liveBadgeCount !== null
    ? liveBadgeCount
    : pendingApprovals.length;

  // Update badge count dynamically
  const configWithBadges = {
    ...navigationConfig,
    sections: navigationConfig.sections.map((section) => ({
      ...section,
      items: section.items.map((item) => {
        if (item.id === 'approvals') {
          return {
            ...item,
            badge: badgeCount > 0 ? badgeCount : undefined,
          };
        }
        return item;
      }),
    })),
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 lg:hidden"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
      >
        {isMobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </Button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        data-tour="sidebar"
        className={cn(
          'fixed top-0 left-0 z-40 h-screen bg-card border-r border-border transition-all duration-300',
          'lg:translate-x-0',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          isCollapsed ? 'w-20' : 'w-64'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            {!isCollapsed && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                  <span className="text-primary-foreground font-bold text-sm">TT</span>
                </div>
                <div>
                  <h2 className="font-semibold text-sm">TimeTrack Pro</h2>
                  <p className="text-xs text-muted-foreground">Professional Services</p>
                </div>
              </div>
            )}
            
            {isCollapsed && (
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center mx-auto">
                <span className="text-primary-foreground font-bold text-sm">TT</span>
              </div>
            )}

            {/* Collapse Toggle - Desktop Only */}
            <Button
              variant="ghost"
              size="icon"
              className="hidden lg:flex h-8 w-8"
              onClick={toggleCollapse}
            >
              {isCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1">
            <ScrollAreaViewport className="h-full">
              <nav className="space-y-6 px-3 py-4">
                {configWithBadges.sections.map((section) => (
                  <div key={section.id}>
                    {section.title && !isCollapsed && (
                      <h3 className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        {section.title}
                      </h3>
                    )}
                    
                    {section.title && isCollapsed && (
                      <div className="mb-2 border-t border-border" />
                    )}

                    <div className="space-y-1">
                      {section.items.map((item) => (
                        <NavigationMenuItem
                          key={item.id}
                          item={item}
                          currentPath={currentPath}
                          onNavigate={(path) => {
                            onNavigate(path);
                            setIsMobileOpen(false);
                          }}
                          collapsed={isCollapsed}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </nav>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>

          {/* Footer */}
          <div className="p-4 border-t border-border">
            {!isCollapsed ? (
              <div className="text-xs text-muted-foreground">
                <p className="font-medium">Version 1.0.0</p>
                <p className="mt-1">© 2024 TimeTrack Pro</p>
              </div>
            ) : (
              <div className="text-center text-xs text-muted-foreground">
                <p>v1.0</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Spacer for main content */}
      <div className={cn('transition-all duration-300', isCollapsed ? 'lg:w-20' : 'lg:w-64')} />
    </>
  );
};
